/**
 * Results presentation rows.
 *
 * A results presentation is one timetable row announcing the final results of
 * one or more categories, in a fixed order (`resultsCategories`). Each
 * category's ranking arrives as an ordinary `timetableDetails` entry under its
 * `detailsUuid`, pushed by the desktop as soon as the final is closed and its
 * results are assigned - so every screen that renders a final's results can
 * render a category of a presentation by looking that entry up instead.
 *
 * Until then the entry carries `resultsStatus` saying why not, and no ranking.
 */
import {
  ResultsStatus,
  TimetableDetails,
  TimetableItem,
  TimetableResultsCategory
} from '../redux/timetableSlice';

const NO_CATEGORIES: TimetableResultsCategory[] = [];

export const isResultsItem = (
  item: TimetableItem | null | undefined
): boolean =>
  item?.itemType === 'results' && Array.isArray(item.resultsCategories);

/** The categories a row announces, in presentation order. */
export const resultsCategoriesOf = (
  item: TimetableItem | null | undefined
): TimetableResultsCategory[] =>
  isResultsItem(item) ? (item?.resultsCategories ?? NO_CATEGORIES) : NO_CATEGORIES;

/**
 * A category of a presentation as a timetable row of its own: the shape the
 * screens built around one round per row expect. Its `ttUuid` is the key the
 * category's results are stored under.
 */
export const categoryRowOf = (
  item: TimetableItem,
  category: TimetableResultsCategory
): TimetableItem => ({
  ...item,
  ttUuid: category.detailsUuid,
  text: category.name,
  categoryColor: category.color,
  itemType: undefined,
  resultsCategories: undefined
});

/**
 * 'unknown' until the desktop has said anything about the category - an older
 * desktop, or a timetable sent before the entry arrived.
 */
export type ResultsAvailability = ResultsStatus | 'unknown';

export const resultsAvailabilityOf = (
  details: TimetableDetails | null | undefined
): ResultsAvailability => details?.resultsStatus ?? 'unknown';

export const isResultsAvailable = (
  details: TimetableDetails | null | undefined
): boolean => details?.resultsStatus === 'available';
